import {ref} from 'vue'
import ModalConfirm from './components/ModalConfirm.vue'

/**
 * VmModal の Promise / queue 制御を担う composable
 *
 * UI描画そのものは外部（VmModal.vue）に委譲する
 *
 * @param {object} options
 * @param {(modalOptions: object) => void} options.present
 * @param {(done: Function) => void} options.dismiss
 * @param {() => void} options.reset
 */
export function useVmModalController({
                                         present,
                                         dismiss,
                                         reset,
                                     }) {
    const queue = []
    const current = ref(null)

    // open / close の多重実行を防ぐためのフラグ
    let isBusy = false

    /**
     * モーダルを表示し、ユーザー操作結果を Promise で返す
     * @param {object} options
     * @returns {Promise<{ action: 'ok' | 'cancel' | 'close' | 'error' | 'dropped',payload: any }>}
     */
    function open(options = {}) {
        if (isBusy) {
            console.warn('モーダル表示中のため open() を無視')

            return Promise.resolve({
                action: 'dropped',
                payload: null,
            })
        }

        return new Promise((resolve) => {
            queue.push({options, resolve})

            if (!current.value) {
                processNext()
            }
        })
    }

    /**
     * confirm 専用ラッパー
     * @param {object} options
     * @returns {Promise<{ action: 'ok' | 'cancel' | 'close', payload: any }>}
     */
    function confirm(options = {}) {
        return open({
            ...options,
            component: options.component || ModalConfirm,
            vars: options.vars || {},
            type: options.type || 'popup',
            close_only_by_btns: true,
        })
    }

    /**
     * 現在表示中のモーダルを閉じる
     * @param {{ action: 'ok' | 'cancel' | 'close', payload: any }} result
     */
    function close(result = {action: 'close', payload: null}) {
        if (isBusy) return;

        setBusy(true);

        dismiss(() => {
            if (current.value?.resolve) {
                current.value.resolve(result)
            }

            reset()
            current.value = null
            setBusy(false);
            processNext()
        })
    }


    /**
     * 現在処理中のモーダル表示要求を失敗として終了する。
     *
     * Promise を error として解決し、
     * 現在の処理状態を解放したうえで
     * queue に残っている次の表示要求を処理する。
     */
    function fail() {
        if (current.value?.resolve) {
            current.value.resolve({
                action: 'error',
                payload: null,
            });
        }

        current.value = null;
        setBusy(false);

        processNext();
    }

    /**
     * queue の先頭を表示する
     */
    function processNext() {
        const item = queue.shift()

        if (!item) {
            current.value = null
            return
        }

        current.value = item
        present(item.options)
    }

    /**
     * モーダル遷移中フラグを切り替える
     * @param {boolean} value
     */
    function setBusy(value) {
        isBusy = value
    }

    return {
        current,
        open,
        confirm,
        close,
        fail,
        setBusy,
    }
}
